// src/components/TitleHandler.tsx
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import titleConfig from '@/config/titleConfig';
import { services, type ServiceCard } from '@/data/services';
import MetaTags from '@/components/MetaTags';
import { serviceFaqs } from '@/data/serviceFaqs';
import {
  getHomePageStructuredData,
  getAboutPageStructuredData,
  getContactPageStructuredData,
  getServicePageStructuredData,
  getFaqStructuredData,
} from '@/config/structuredDataConfig';

/**
 * Component that handles document title, meta description, Open Graph tags,
 * and structured data based on current route
 */
const TitleHandler = () => {
  const location = useLocation();

  // Extract current page data
  const getPageData = () => {
    // Get the base route without query parameters
    const path = location.pathname;

    // Default meta title and description
    let title = 'Hoptira';
    let description =
      'A digital agency that simplifies web, apps, analytics, ai, and automation';
    let structuredData;
    let image = '/hoptira-logo-full-dark.svg';
    let type = 'website';
    let canonicalUrl;

    // Check if this is a service detail page (using URL parameters)
    const isServicePage =
      path === '/services' && location.search.includes('tab=');

    if (isServicePage) {
      const params = new URLSearchParams(location.search);
      const serviceId = params.get('tab');

      // Find the service name from the serviceId
      const service = services.find((s: ServiceCard) => s.id === serviceId);

      if (service) {
        const serviceMeta = titleConfig.getServiceMeta(service.title);
        title = serviceMeta.title;
        description = serviceMeta.description;
        image = `/services-${serviceId}.svg`;
        type = 'article';

        // Set canonical URL for service pages
        canonicalUrl = `https://hoptira.com/services?tab=${serviceId}`;

        // Get structured data for this specific service
        structuredData = getServicePageStructuredData(serviceId || undefined);

        // Add FAQ structured data if available
        const serviceFaqGroup = serviceFaqs.find(
          (group) => group.id === serviceId,
        );
        if (serviceFaqGroup) {
          structuredData = [
            structuredData,
            getFaqStructuredData(serviceFaqGroup.faqs),
          ];
        }

        return {
          title,
          description,
          image,
          type,
          canonicalUrl,
          structuredData,
        };
      }
    }

    // Handle normal routes
    const pageConfig =
      titleConfig.pages[path as keyof typeof titleConfig.pages];

    if (pageConfig) {
      title = pageConfig.title;
      description = pageConfig.description || description;

      // Set structured data and canonical URL based on route
      switch (path) {
        case '/':
          structuredData = getHomePageStructuredData();
          canonicalUrl = 'https://hoptira.com/';
          break;
        case '/about':
          structuredData = getAboutPageStructuredData();
          canonicalUrl = 'https://hoptira.com/about';
          break;
        case '/services':
          structuredData = getServicePageStructuredData();
          canonicalUrl = 'https://hoptira.com/services';
          break;
        case '/contact':
          structuredData = getContactPageStructuredData();
          canonicalUrl = 'https://hoptira.com/contact';
          type = 'profile';
          break;
        case '/privacy-policy':
          type = 'article';
          canonicalUrl = 'https://hoptira.com/privacy-policy';
          break;
        case '/terms':
          type = 'article';
          canonicalUrl = 'https://hoptira.com/terms';
          break;
        case '/sitemap':
          canonicalUrl = 'https://hoptira.com/sitemap';
          break;
        default:
          structuredData = {};
        // For other pages, let the default canonical URL logic work
      }
    } else {
      // Default to 404 for unknown routes
      const notFoundConfig = titleConfig.pages['/404'];
      title = notFoundConfig.title;
      description = notFoundConfig.description || description;
    }

    return { title, description, image, type, canonicalUrl, structuredData };
  };

  const { title, description, image, type, canonicalUrl, structuredData } =
    getPageData();
  const fullTitle = `${title} ${titleConfig.titleSuffix}`;

  useEffect(() => {
    // Set document title
    document.title = fullTitle;
  }, [fullTitle]);

  // Return the MetaTags component with our data
  return (
    <MetaTags
      title={fullTitle}
      description={description}
      image={image}
      type={type}
      canonicalUrl={canonicalUrl}
      structuredData={structuredData}
    />
  );
};

export default TitleHandler;
